import React from "react";
import { Link } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import { SITE_URL } from "@/lib/api";
import { orgGraph, providerRef, webPage } from "@/lib/schema";
import { WARRANTY_SPOKES } from "@/data/warrantyCluster";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import LeadForm from "@/components/LeadForm";
import {
  ShieldCheck, Phone, ArrowRight, HeartHandshake, PiggyBank, Wrench,
  ClipboardCheck, Cog, FileCheck2, LifeBuoy, CheckCircle2, Info,
  Snowflake, Flame, Box, Wind, BookOpen,
} from "lucide-react";

export default function LifetimeWarranty() {
  const { company } = useCompany();

  const faqs = [
    { q: "What is the Aquila Air & Heating Lifetime Warranty?", a: "It is additional Lifetime Warranty protection available to qualifying customers who purchase and install a new HVAC system through Aquila Air & Heating. Coverage applies to qualifying equipment and is subject to specific terms, conditions, limitations, and exclusions. The exact coverage depends on the equipment you choose and the program qualifications for your specific system." },
    { q: "Which systems qualify for the Lifetime Warranty?", a: "Lifetime Warranty protection is available on qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits installed by Aquila Air & Heating. Our comfort specialists will confirm the coverage available for your specific system before your installation is complete." },
    { q: "How do I know if my new HVAC system qualifies?", a: "Qualification depends on the specific equipment you select and the program requirements that apply to it. Our comfort specialists will review the available Lifetime Warranty protection for your chosen system before your installation is complete." },
    { q: "Is every HVAC system eligible for additional warranty protection?", a: "Availability varies by equipment type and program qualifications. Qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits may be eligible for additional Lifetime Warranty protection. We'll let you know exactly what is available for your specific system." },
    { q: "Do I need to register my system?", a: "Registration may be required for certain coverage to apply. Our team will explain any registration requirements associated with your system before installation, and we recommend keeping your documentation for your records." },
    { q: "What information will I receive about my warranty coverage?", a: "Before your installation is complete, our team will review the available Lifetime Warranty protection associated with your specific system, including applicable terms and any registration or eligibility requirements. You should retain your installation and warranty documentation." },
    { q: "Does maintenance affect warranty eligibility?", a: "Proper installation and ongoing maintenance can play an important role in HVAC performance and reliability, and some coverage may include maintenance-related eligibility requirements. Our team will explain any requirements that apply to your system." },
    { q: "Can Aquila Air & Heating explain my warranty options before I purchase?", a: "Absolutely. We'll review qualifying equipment and the Lifetime Warranty protection that may be available for your investment before you make a decision, so you have the information you need up front." },
  ];

  const jsonLd = [
    ...orgGraph(company),
    webPage("/lifetime-warranty/", "Lifetime HVAC Warranty Protection", "Additional Lifetime Warranty protection available to qualifying customers who purchase new HVAC systems through Aquila Air & Heating."),
    {
      "@context": "https://schema.org", "@type": "Service",
      name: "Lifetime HVAC Warranty Protection",
      serviceType: "HVAC Lifetime Warranty Protection",
      description: "Additional Lifetime Warranty protection available to qualifying customers who purchase new HVAC systems through Aquila Air & Heating. Available on qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits.",
      provider: providerRef(),
      areaServed: company.primary_service_area,
      url: `${SITE_URL}/lifetime-warranty/`,
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  const benefits = [
    { i: HeartHandshake, t: "Added Peace of Mind", d: "Feel more confident knowing additional protection may be available for qualifying equipment." },
    { i: PiggyBank, t: "Protection for Your Investment", d: "Your HVAC system is a major investment. Lifetime Warranty coverage can help provide additional long-term value." },
    { i: Wrench, t: "Professional Installation Matters", d: "Proper system selection, installation, commissioning, and maintenance all play an important role in HVAC performance and long-term reliability." },
  ];

  const steps = [
    { n: "01", i: ClipboardCheck, t: "Choose a Qualifying System", d: "Our comfort specialists will help you select an HVAC system designed around your home's comfort needs." },
    { n: "02", i: Cog, t: "Professional Installation", d: "Your system is installed and commissioned by the Aquila Air & Heating team according to manufacturer and applicable installation requirements." },
    { n: "03", i: FileCheck2, t: "Receive Your Lifetime Warranty Protection", d: "Qualifying systems may be eligible for additional Lifetime Warranty protection. Our team will explain the available coverage and registration requirements before your installation is complete." },
  ];

  const qualifyingEquipment = [
    { i: Snowflake, t: "Split Systems", d: "Central air conditioner and air handler combinations for whole-home comfort." },
    { i: Flame, t: "Gas Furnaces", d: "High-efficiency gas heating for dependable warmth on cool Space Coast nights." },
    { i: Box, t: "Package Units", d: "All-in-one heating and cooling systems ideal for many Florida homes." },
    { i: Wind, t: "Mini Splits", d: "Ductless systems for room-by-room comfort, additions, and garages." },
  ];

  const knowPoints = [
    "Lifetime Warranty protection is available on qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits.",
    "Coverage availability depends on the specific HVAC system and program qualifications.",
    "Terms, conditions, limitations, and exclusions apply.",
    "Warranty coverage may vary based on equipment type, installation requirements, registration, maintenance, and other eligibility requirements.",
    "Our team will review the available Lifetime Warranty protection associated with your specific system before installation.",
    "Customers should retain their installation and warranty documentation.",
  ];

  const whyCards = [
    { i: ClipboardCheck, t: "Proper System Design" },
    { i: Cog, t: "Professional Installation" },
    { i: FileCheck2, t: "Clear Warranty Information" },
    { i: LifeBuoy, t: "Long-Term Customer Support" },
  ];

  return (
    <>
      <Seo
        title="Lifetime HVAC Warranty Protection | Aquila Air & Heating"
        description="Protect your new HVAC investment with a Lifetime Warranty from Aquila Air & Heating. Available on qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits."
        path="/lifetime-warranty/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="warranty-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5463581/pexels-photo-5463581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200"
            alt="Aquila Air & Heating technician installing a new air conditioning system in a Florida home"
            className="w-full h-full object-cover opacity-25"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-24">
          <div className="[&_*]:!text-slate-300">
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Lifetime Warranty" }]} />
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch">
            <ShieldCheck className="h-4 w-4" /> HVAC Lifetime Warranty Protection
          </p>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            The Aquila Lifetime Warranty
          </h1>
          <p className="mt-4 max-w-2xl text-2xl sm:text-3xl font-heading font-semibold text-white">
            More Confidence. More Protection. More Peace of Mind.
          </p>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">
            Your heating and air conditioning system is one of the biggest investments in your home. Aquila Air &amp; Heating offers qualifying customers a Lifetime Warranty designed to provide greater confidence in their new HVAC system &mdash; available on qualifying Split Systems, Gas Furnaces, Package Units, and Mini Splits.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact-us"
              onClick={() => trackEvent("warranty_cta_click", { location: "hero", action: "explore" })}
              data-testid="warranty-hero-explore"
              className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors"
            >
              Explore Your Warranty Options <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href={`tel:${company.phone_href || ""}`}
              onClick={() => trackEvent("phone_click", { location: "warranty_hero" })}
              data-testid="warranty-hero-call"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3.5 px-8 rounded-md transition-colors"
            >
              <Phone className="h-5 w-5" /> Call Aquila Air &amp; Heating
            </a>
          </div>
        </div>
      </section>

      {/* Section 2 — Why warranty protection matters */}
      <section className="py-16 md:py-24" data-testid="warranty-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Long-Term Value</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Your Comfort System Deserves Long-Term Protection</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              A new HVAC system is designed to provide years of dependable comfort. However, unexpected equipment issues can happen over time. A Lifetime Warranty may help provide homeowners with greater peace of mind and added protection for qualifying HVAC equipment.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.i;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid={`warranty-benefit-${i}`}>
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-cool/10 text-cool"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-xl font-semibold text-navy">{b.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section — Qualifying equipment */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="warranty-equipment">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">What Qualifies</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Lifetime Warranty Is Available On</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Qualifying systems installed by Aquila Air &amp; Heating may be eligible for our Lifetime Warranty protection. Ask our team to confirm the coverage available for the system you choose.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualifyingEquipment.map((e, i) => {
              const Icon = e.i;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={`warranty-equipment-${i}`}>
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-launch/10 text-launch"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-lg font-semibold text-navy">{e.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3 — How the program works */}
      <section className="py-16 md:py-24 bg-white" data-testid="warranty-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Simple Process</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">How the Program Works</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => {
              const Icon = s.i;
              return (
                <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-xl p-7" data-testid={`warranty-step-${i}`}>
                  <span className="absolute top-5 right-6 font-heading text-4xl font-extrabold text-slate-200">{s.n}</span>
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-navy text-launch"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-xl font-semibold text-navy">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4 — What homeowners should know */}
      <section className="py-16 md:py-24" data-testid="warranty-know">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Clear &amp; Honest</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Simple, Straightforward Protection</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              We believe in giving homeowners the facts up front. Here's what to keep in mind about the Lifetime Warranty protection available for your system.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm">
            <ul className="space-y-4">
              {knowPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`warranty-know-${i}`}>
                  <CheckCircle2 className="h-5 w-5 text-cool mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 5 — Why choose Aquila */}
      <section className="py-16 md:py-24 bg-navy text-white dot-grid" data-testid="warranty-choose">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">The Launch Difference</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">The Right Installation Is Where Protection Begins</h2>
            <p className="mt-4 text-lg text-slate-200 leading-relaxed">
              At Aquila Air &amp; Heating, we believe protecting your investment starts long before a warranty is ever needed. Proper equipment sizing, professional installation, attention to airflow, system commissioning, and ongoing maintenance can all contribute to the performance and longevity of your HVAC system.
            </p>
            <p className="mt-4 text-slate-200 leading-relaxed">
              Our goal is simple: help you choose the right comfort system and provide the information you need to understand the Lifetime Warranty protection available for your investment.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {whyCards.map((c, i) => {
              const Icon = c.i;
              return (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-6 text-center" data-testid={`warranty-choose-${i}`}>
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-white/10 text-launch mx-auto"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-base font-semibold text-white">{c.t}</h3>
                </div>
              );
            })}
          </div>
          {/* Contextual internal links */}
          <div className="mt-10 flex flex-wrap gap-3">
            {[
              { to: "/ac-replacement", label: "AC Replacement" },
              { to: "/ac-installation", label: "AC Installation" },
              { to: "/heating-installation", label: "Heating Installation" },
              { to: "/financing", label: "Financing Options" },
            ].map((l) => (
              <Link key={l.to} to={l.to} data-testid={`warranty-link-${l.to.slice(1)}`} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 text-sm font-medium text-white transition-colors">
                {l.label} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section — Lifetime Warranty Authority Cluster hub */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="warranty-cluster-hub">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">Learn More</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Everything About the Lifetime Warranty</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Dig deeper into how our Lifetime Warranty protection works — what qualifies, how it compares to a standard manufacturer warranty, the maintenance that keeps it active, and what it means for Brevard County homeowners.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {WARRANTY_SPOKES.map((s) => (
              <Link
                key={s.slug}
                to={`/lifetime-warranty/${s.slug}`}
                data-testid={`warranty-cluster-${s.slug}`}
                className="group bg-white border border-slate-200 hover:border-cool rounded-xl p-6 shadow-sm transition-colors"
              >
                <div className="flex items-start gap-4">
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-md bg-launch/10 text-launch shrink-0"><BookOpen className="h-5 w-5" /></span>
                  <div>
                    <h3 className="text-lg font-semibold text-navy flex items-center gap-1.5">{s.h1} <ArrowRight className="h-4 w-4 text-launch group-hover:translate-x-1 transition-transform" /></h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.subhead}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6 — FAQ */}
      <div className="bg-slate-100"><FaqAccordion faqs={faqs} title="Warranty Questions, Answered" /></div>

      {/* Section 7 — Final CTA */}
      <section className="py-16 md:py-24" data-testid="warranty-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy tracking-tight">Ask About the Lifetime Warranty for Your New HVAC System</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              When you are ready to replace your heating and air conditioning system, ask the Aquila Air &amp; Heating team about qualifying equipment and the Lifetime Warranty protection that may be available.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#warranty-consult"
                onClick={() => trackEvent("warranty_cta_click", { location: "final", action: "consult" })}
                data-testid="warranty-final-consult"
                className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-md transition-colors"
              >
                Schedule a Free Consultation <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href={`tel:${company.phone_href || ""}`}
                onClick={() => trackEvent("phone_click", { location: "warranty_final" })}
                data-testid="warranty-final-call"
                className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold py-3.5 px-8 rounded-md transition-colors"
              >
                <Phone className="h-5 w-5" /> Call Us Today
              </a>
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-4">
              <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500 leading-relaxed" data-testid="warranty-disclaimer">
                Lifetime Warranty availability, coverage, eligibility, terms, conditions, limitations, and exclusions vary by qualifying equipment and program requirements. Available on qualifying systems. Details will be provided for applicable systems. This webpage is for general informational purposes and does not modify or replace the terms of any applicable warranty documentation.
              </p>
            </div>
          </div>
          <div id="warranty-consult" className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-navy">Request Your Free Consultation</h3>
            <p className="text-sm text-slate-600 mt-1 mb-5">Tell us about your project and we'll review qualifying systems and available protection with you.</p>
            <LeadForm defaultService="New System & Warranty" compact />
          </div>
        </div>
      </section>
    </>
  );
}
